import { Injectable } from '@angular/core';

import { Observable, forkJoin, interval } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import dayjs from 'dayjs';

import { ApiErrorsService } from '@core/services/api/api-errors.service';
import { ArrestService } from '@core/services/arrests/arrest.service';

import { IReqDefaultAccount } from '@pages/other/profile/transfer-and-payments/default-accounts/_models/default-accounts.model';

import { IReopenAvailableType } from '@shared/models/card-actions.model';
import { CreditPaymentData, CreditPaymentDataFromServer } from '@shared/models/credit-payment.model';
import { IconListItem } from '@shared/models/icon-list-item.model';
import { NewCard, NewCardBranch } from '@shared/models/new-card.model';
import { DocumentSettingsFromServer, SuccessfulPaymentCreation } from '@shared/models/payment.model';
import { ProductCard, ProductMessage } from '@shared/models/product-card.model';
import { Product, ProductActions, ProductCloseData, ProductTypes, ProductsList } from '@shared/models/products.model';
import { ShortProductCard } from '@shared/models/short-product-card.model';

import { AppSettingsService } from '../app-settings/app-settings.service';
import { CardAdditionalService } from './card-additional.service';
import { CardService } from './card.service';
import { GetCreditPaymentScheduleCommand } from './commands/get-credit-payment-schedule.command';
import { GetProductAboutCommand } from './commands/get-product-about.command';
import { GetProductRequisitesCommand } from './commands/get-product-requisites.command';
import { ProductsApiService } from './products-api.service';
import { ProductsBaseService } from './products-base.service';
import { ProductsCloseService } from './products-close.service';
import { ProductsFilterService } from './products-filter.service';
import { ProductsTransformService } from './products-transform.service';

// Временный тип для запросов к документам
type DocumentRequestData = Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class ProductsServiceRefactored extends ProductsBaseService {
  private types = [
    { title: 'Карты', type: 'card' },
    { title: 'Вклады', type: 'deposit' },
    { title: 'Счета', type: 'account' },
    { title: 'Накопительные счета', type: 'saveAccount' },
    { title: 'Инвестиции', type: 'investment' },
    { title: 'Кредиты', type: 'credit' }
  ];

  constructor(
    private productsApiService: ProductsApiService,
    private productsTransformService: ProductsTransformService,
    private cardService: CardService,
    private productsCloseService: ProductsCloseService,
    private productsFilterService: ProductsFilterService,
    private apiErrorsService: ApiErrorsService,
    private appSettings: AppSettingsService,
    private arrestService: ArrestService,
    private cardAdditionalService: CardAdditionalService,
    private getProductAboutCommand: GetProductAboutCommand,
    private getProductRequisitesCommand: GetProductRequisitesCommand,
    private getCreditPaymentScheduleCommand: GetCreditPaymentScheduleCommand
  ) {
    super();
    dayjs.locale('ru');
  }

  /**
   * Получение продуктов
   */
  public getProducts(refresh = false): Observable<Product[]> {
    this.productsLoaded$.next(false);
    if (!this.timer) {
      this.startProductsCacheTimer();
      this.timer = true;
    }

    return Observable.create((observer) => {
      if (this.products && this.products.length && !refresh) {
        this.productsLoaded$.next(true);
        observer.next(this.products);

        return observer.complete();
      }

      forkJoin([
        this.productsApiService.loadProducts(),
        this.appSettings.getSetting('enableBlockedProducts')
      ]).subscribe(
        ([products, enableBlockedProducts]) => {
          this.productsFilterService.setEnableBlockedProducts(!!enableBlockedProducts);
          this.products = this.productsTransformService
            .prepareProducts(products, false)
            .filter(this.productsFilterService.filteringBySetting.bind(this.productsFilterService));

          this.generateProductCards();
          this.generateProductsList();
          this.productsLoaded$.next(true);
          observer.next(this.products);

          return observer.complete();
        },
        (error) => {
          this.productsLoaded$.next(true);
          this.apiErrorsService.errorHandler(error);
          observer.error(error);
        }
      );
    });
  }

  /**
   * Генерация карточек продуктов
   */
  public generateProductCards(): ProductCard[] {
    const productCards = this.productsTransformService.generateProductCards(this.products, this.types);

    this.productCards$.next(productCards);

    return productCards;
  }

  /**
   * Генерация списка продуктов
   */
  public generateProductsList(): ProductsList[] {
    const productsList = this.productsTransformService.generateProductsList(this.products, this.types);

    this.productsList$.next(productsList);

    return productsList;
  }

  /**
   * Получение карточек продуктов
   */
  public getProductCards(): Observable<ProductCard[]> {
    return Observable.create((observer) => {
      if (this.products && this.products.length) {
        observer.next(this.generateProductCards());

        return observer.complete();
      }
      this.getProducts().subscribe((_products: Product[]) => {
        observer.next(this.generateProductCards());

        return observer.complete();
      });
    });
  }

  /**
   * Получение списка продуктов
   */
  public getProductsList(): Observable<ProductsList[]> {
    return Observable.create((observer) => {
      if (this.products && this.products.length) {
        observer.next(this.generateProductsList());

        return observer.complete();
      }
      this.getProducts().subscribe((_products: Product[]) => {
        observer.next(this.generateProductsList());

        return observer.complete();
      });
    });
  }

  /**
   * Получение коротких карточек продуктов
   */
  public getShortProductCards(): Observable<ShortProductCard[]> {
    return Observable.create((observer) => {
      if (this.products && this.products.length) {
        observer.next(this.productsTransformService.generateShortProductCards(this.products));

        return observer.complete();
      }
      this.getProducts().subscribe((products: Product[]) => {
        observer.next(this.productsTransformService.generateShortProductCards(products));

        return observer.complete();
      });
    });
  }

  /**
   * Получение действий с продуктом
   */
  public getProductActions(product: Product): Observable<ProductActions> {
    // Инициализируем пустые массивы действий (как в оригинале)
    const actions: IconListItem[] = [];
    const infoActions: IconListItem[] = [];

    // Проверяем возможность пополнения и удаляем пункт, если таковой нет
    if (
      ['account', 'card', 'deposit'].includes(product.productType) &&
      !this.productsFilterService.checkReplenishmentPossibility(product)
    ) {
      const index = actions.findIndex((item) => item.type === 'replenish');

      if (index !== -1) actions.splice(index, 1);
    }

    // Проверяем, если данный счет не брокерский, то удаляем возможность вывода
    if (!product.isBrokerAccount) {
      const idx = actions.findIndex((v) => v.type === 'withdraw');

      if (idx !== -1) actions.splice(idx, 1);
    }

    // Проверяем возможность платежа или перевода и удаляем пункт, если таковой нет
    if (
      ['account', 'card', 'deposit'].includes(product.productType) &&
      !this.productsFilterService.checkPaymentOrTransferPossibility(product)
    ) {
      const index = actions.findIndex((item) => item.type === 'paymentOrTransfer');

      if (index !== -1) actions.splice(index, 1);
    }

    if (product.isVirtual || this.cardService.checkOnMomentCard(product)) {
      const idx = actions.findIndex((v) => v.type === 'virtual');

      if (idx !== -1) actions.splice(idx, 1);
    }

    // Меняем тип пункта у карт в зависимости от статуса
    if (product.productType === 'card' && product.status === 'block') {
      const idx = actions.findIndex((v) => v.type === 'virtual');

      if (idx !== -1) actions.splice(idx, 1);

      const action = infoActions.find((item) => item.type === 'lockCard');

      if (action) {
        action.title = 'Разблокировать карту';
        action.type = 'unlockCard';
      }
    }

    // Проверяем возможность закрывать счет
    if (this.productsCloseService.checkCloseAccountPossibility(product)) {
      const index = actions.findIndex((item) => item.type === 'productClose');

      if (index !== -1) actions.splice(index, 1);
    }

    // Если это кредитная карта, то необходимо убрать кнопку "Закрыть"
    if (product.productType === 'card' && product.type === '5') {
      const index = infoActions.findIndex((item) => item.type === 'productClose');

      if (index !== -1) infoActions.splice(index, 1);
    }

    if (product.productType === 'deposit' && !this.productsFilterService.checkAvailableDebitFromDeposit(product)) {
      const index = actions.findIndex((item) => item.type === 'debit-from-deposit');

      if (index !== -1) actions.splice(index, 1);
    }

    if (!product.isVirtual) {
      const virtualPinIndex = actions.findIndex((item) => item.type === 'virtualPin');

      if (virtualPinIndex !== -1) actions.splice(virtualPinIndex, 1);

      const virtualCvvIndex = actions.findIndex((item) => item.type === 'virtualCVV');

      if (virtualCvvIndex !== -1) actions.splice(virtualCvvIndex, 1);
    }

    // Заменено на реактивный вызов
    // const arrestExists = this.arrestService.getStayByAccNumber$(product.number);
    // TODO: Требуется рефакторинг этого метода для работы с Observable

    // Временное решение - удаляем проверку арестов
    const idx = actions.findIndex((item) => item.type === 'arrest');
    if (idx !== -1) {
      actions.splice(idx, 1);
    }

    return this.productsFilterService.checkActionsAvailability(product, { actions, infoActions });
  }

  /**
   * Закрытие продукта
   */
  public closeProduct(id: string): Observable<{ result: boolean; product: Product; navParams: ProductCloseData }> {
    return this.productsCloseService.closeProduct(id, this.products);
  }

  /**
   * Изменение псевдонима продукта
   */
  public changeProductAlias(id: string, alias: string): Observable<boolean> {
    return this.productsApiService.changeProductAlias(id, alias).pipe(
      map((res: boolean) => {
        if (!res) return false;

        const product = this.findProductById(this.products, id);

        if (product) {
          product.alias = alias;
          this.generateProductsList();

          return true;
        }

        return false;
      })
    );
  }

  /**
   * Получение данных о платеже по кредиту
   */
  public getCreditPaymentData(id: string): Observable<CreditPaymentData> {
    return this.productsApiService.createCreditPayment({}).pipe(
      map((data: CreditPaymentDataFromServer) => {
        const accIdProducts = this.productsTransformService.prepareProducts(data.accIdProducts, false);

        const creditPaymentData: CreditPaymentData = {
          accIdCards: this.productsTransformService
            .generateShortProductCards(accIdProducts)
            .filter((item) => item.id !== id),
          docSettings: data.docSettings
        };

        return creditPaymentData;
      })
    );
  }

  /**
   * Получение данных для блокировки карты
   */
  public getLockCardData() {
    return this.cardService.getLockCardData();
  }

  /**
   * Получение данных для разблокировки карты
   */
  public getUnlockCardData() {
    return this.cardService.getUnlockCardData();
  }

  /**
   * Запуск таймера кеша продуктов
   */
  public startProductsCacheTimer(): void {
    const timer$ = interval(1000);
    const timeUntilResetCache = 300; // 5 min

    timer$
      .pipe(
        takeUntil(this.resetCacheTimer$),
        take(timeUntilResetCache),
        map((sec) => timeUntilResetCache - 1 - sec)
      )
      .subscribe((timer) => {
        if (timer === 0) {
          this.resetProductsCacheTimer();
        }
      });
  }

  /**
   * Получение информации о продукте по ID
   */
  public getProductInfoById(id: string): string {
    const product = this.findProductById(this.products, id);

    if (!product) {
      return '';
    }

    return this.productsTransformService.getProductInfo(product, product.productType as ProductTypes);
  }

  /**
   * Получение валюты продукта по ID
   */
  public getProductCurrencyById(id: string): string {
    const product = this.findProductById(this.products, id);

    if (!product) {
      return '';
    }

    return product.currency;
  }

  /**
   * Получение информации о продукте
   */
  public getProductAbout(id: string) {
    return this.getProductAboutCommand.execute(id);
  }

  /**
   * Получение реквизитов продукта
   */
  public getProductRequisites(id: string) {
    return this.getProductRequisitesCommand.execute(id);
  }

  /**
   * Получение графика платежей по кредиту
   */
  public getCreditPaymentSchedule(id: string) {
    return this.getCreditPaymentScheduleCommand.execute(id);
  }

  /**
   * Получение продукта по ID
   */
  public getProduct(id: string): Observable<Product | undefined | unknown> {
    return Observable.create((observer) => {
      if (this.products && this.products.length) {
        observer.next(this.findProductById(this.products, id));

        return observer.complete();
      }
      this.getProducts().subscribe((_products: Product[]) => {
        observer.next(this.findProductById(this.products, id));

        return observer.complete();
      });
    });
  }

  /**
   * Получение данных для переоткрытия карты
   */
  public getReopenCardData(productCode: string) {
    return this.cardAdditionalService.getReopenCardData(productCode);
  }

  /**
   * Получение причин для переоткрытия карты
   */
  public getReopenCardReasons(codeMain: string, codeAdd: string) {
    return this.cardAdditionalService.getReopenCardReasons(codeMain, codeAdd);
  }

  /**
   * Получение сертификата для изменения ПИН-кода
   */
  public getCertificateForChangePin() {
    return this.cardAdditionalService.getCertificateForChangePin();
  }

  /**
   * Изменение ПИН-кода карты
   */
  public changeCardPin(data: { cardId: string; encryptedPin: string }) {
    return this.cardAdditionalService.changeCardPin(data);
  }

  /**
   * Проверка доступности мобильного кошелька
   */
  public checkMobileWallet() {
    return this.cardAdditionalService.checkMobileWallet();
  }

  /**
   * Доступно ли повышение брокерского счета
   */
  public availableForUpBrokerAccount(number: string) {
    return this.cardAdditionalService.availableForUpBrokerAccount(number);
  }

  /**
   * Проверка на брокерский счет
   */
  public checkOnBrokerAccount(number: string) {
    return this.cardAdditionalService.checkOnBrokerAccount(number);
  }

  /**
   * Проверка на залоговый счет
   */
  public checkOnCollateralAccount(productSubType: string) {
    return this.cardAdditionalService.checkOnCollateralAccount(productSubType);
  }

  /**
   * Получение дат окончания продукта
   */
  public getProductEndDate(product: Product): string {
    return this.productsTransformService.getProductEndDate(product);
  }

  /**
   * Получение иконки продукта
   */
  public getProductIcon(product: Product, type: ProductTypes): string {
    return this.productsTransformService.getProductIcon(product, type);
  }

  /**
   * Получение сообщения о продукте
   */
  public getProductMessage(product: Product): ProductMessage {
    return this.productsTransformService.getProductMessage(product);
  }

  /**
   * Получение номера сортировки продукта
   */
  public getProductSortNumber(product: Product, type: ProductTypes): string {
    return this.productsTransformService.getProductSortNumber(product, type);
  }

  /**
   * Получение заголовка продукта
   */
  public getProductTitle(product: Product, type: ProductTypes): string {
    return this.productsTransformService.getProductTitle(product, type);
  }

  /**
   * Получение продуктов по типу
   */
  public getProductsByType(type: ProductTypes) {
    return this.products.filter((item: Product) => item.productType === type);
  }

  /**
   * Получение короткой карточки продукта из бэк-офиса
   */
  public getShortProductCardInBackOffice(branchID?: number | string) {
    return this.productsApiService.getShortProductCardInBackOffice(branchID);
  }

  /**
   * Сброс таймера кеша продуктов
   */
  public resetProductsCacheTimer() {
    this.cleanCache = true;
    this.resetCacheTimer$.next(null);
    this.startProductsCacheTimer();
  }

  /**
   * Выбор продукта
   */
  public selectProduct(id: string, primary = true) {
    this.selectedProductId = id;
    this.isPrimary = primary;
  }

  /**
   * Получение выбранного продукта
   */
  public getSelectedProduct(): { id: string; isPrimary: boolean } {
    const id = this.selectedProductId;
    const isPrimary = this.isPrimary;

    return { id, isPrimary };
  }

  /**
   * Установка счета по умолчанию
   */
  public setDefaultAccount(data: IReqDefaultAccount): Observable<string> {
    return this.productsApiService.setDefaultAccount(data);
  }

  /**
   * Отображение ошибки закрытия продукта
   */
  public showProductCloseError(type: string, currencyCode: string) {
    return this.productsCloseService.showProductCloseError(type, currencyCode);
  }

  /**
   * Очистка продуктов
   */
  public clearProducts() {
    console.log('clear products');
    this.products = null;
    this.productCards$.next([]);
    this.productsList$.next([]);
    this.productsLoaded$.next(false);
    this.selectedProductId = null;
  }

  /**
   * Получение списка карт с дополнительными картами
   */
  public getCardsListWithAdditionalCards() {
    return this.cardService.getCardsListWithAdditionalCards(this.products);
  }

  /**
   * Получение HTML информации о продукте
   */
  public getHTMLProductInfo(product: Product, type: ProductTypes): string {
    return this.productsTransformService.getHTMLProductInfo(product, type);
  }

  /**
   * Получение данных для закрытия счета
   */
  public getAccountCloseData(mode: string) {
    return this.productsApiService.getAccountCloseData(mode);
  }

  /**
   * Получение данных для закрытия депозита
   */
  public getDepositCloseData(mode: string) {
    return this.productsApiService.getDepositCloseData(mode);
  }

  /**
   * Получение данных для закрытия карты
   */
  public getCardCloseData(mode: string) {
    return this.productsApiService.getCardCloseData(mode);
  }

  /**
   * Генерация элементов списка продуктов
   */
  public generateProductsListItems(type: ProductTypes) {
    return this.productsTransformService.generateProductsListItems(this.products, type);
  }

  /**
   * Загрузка продуктов
   */
  public loadProducts() {
    return this.productsApiService.loadProducts();
  }

  /**
   * Блокировка карты
   */
  public lockCard(data: DocumentRequestData): Observable<DocumentSettingsFromServer | SuccessfulPaymentCreation> {
    return this.cardService.lockCard(data);
  }

  /**
   * Разблокировка карты
   */
  public unlockCard(data: DocumentRequestData): Observable<DocumentSettingsFromServer | SuccessfulPaymentCreation> {
    return this.cardService.unlockCard(data);
  }

  /**
   * Получение филиалов
   */
  public getBranches(): Observable<NewCardBranch[]> {
    return this.cardAdditionalService.getBranches();
  }

  /**
   * Получение информации о продукте
   */
  public getProductInfo(product: Product, type: ProductTypes): string {
    return this.productsTransformService.getProductInfo(product, type);
  }

  /**
   * Загрузка доступных типов карт для переоткрытия
   */
  public loadAvailableTypesForReopenCardByProductCode(code: string): Observable<IReopenAvailableType[]> {
    return this.cardAdditionalService.loadAvailableTypesForReopenCardByProductCode(code);
  }

  /**
   * Загрузка данных для переоткрытия карты
   */
  public loadReopenCardData(data?: Record<string, unknown>): Observable<{ docSettings: unknown[] }> {
    return this.cardAdditionalService.loadReopenCardData(data);
  }

  /**
   * Подготовка продуктов
   */
  public prepareProducts(data: Record<string, unknown>, isForSelect = false): Product[] {
    return this.productsTransformService.prepareProducts(data, isForSelect);
  }

  /**
   * Генерация коротких карточек продуктов
   */
  public generateShortProductCards(products: Product[]): ShortProductCard[] {
    return this.productsTransformService.generateShortProductCards(products);
  }

  /**
   * Проверка на ES-счет
   */
  public checkESAccount(number: string): boolean {
    return this.productsFilterService.checkESAccount(number);
  }

  /**
   * Проверка продукта на возможность добавления дополнительной карты
   */
  public checkProductOnAdditionalCard(product: Product, cardData: NewCard): boolean {
    return this.cardService.checkProductOnAdditionalCard(product, cardData);
  }
}
